<!--
 * @Author: DQL
 * @Date: 2022-04-08 11:37:09
 * @LastEditors: DQL
 * @LastEditTime: 2022-12-27 09:45:50
 * @Description: file content
-->
<template>
  <el-upload
    ref="fileServeUpload"
    :headers="headers"
    :show-file-list="showFileList"
    :before-upload="handleBeforeUpload"
    :on-change="handleChangeFile"
    :on-success="handleSuccess"
    :on-error="handleError"
    :on-remove="handleRemove"
    :on-preview="handlePreview"
    :action="fileAction"
    :file-list="fileList"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <!-- 默认内容 -->
    <template v-slot:default>
      <slot name="default" />
    </template>
    <!-- 触发文件选择框的内容 -->
    <template v-slot:trigger>
      <slot name="trigger" />
    </template>
    <!-- 提示说明文字 -->
    <template v-slot:tip>
      <slot name="tip" />
    </template>
    <!-- ElementUI upload的API文档中暂未提供的slot="file"说明，在例子中有使用 -->
    <template v-slot:file="slotProps">
      <slot
        name="file"
        :file="slotProps.file"
      />
    </template>
  </el-upload>
</template>

<script>
export default {
  name: 'FileServeUpload',
  inheritAttrs: false,
  props: {
    showFileList: { // 是否显示文件列表
      type: Boolean,
      default: false
    },
    successAlertMsg: { // 上传成功后的弹出内容
      type: String,
      default: ''
    },
    format: { // 支持的文件类型，与 accept 不同的是，format 是识别文件的后缀名，accept 为 input 标签原生的 accept 属性，会在选择文件时过滤，可以两者结合使用，如“zip,jpg,png,word”
      type: Array,
      default: () => []
    },
    multipleFile: {
      type: Boolean,
      default: false
    },
    maxSize: {
      type: Number,
      default: 0
    },
    value: {
      type: [String, Array],
      default: ''
    }
  },
  data () {
    return {
      loading: null,
      headers: { // 请求头部配置
        [this.$config.serverHeaderTokenName]: this.$HMCookies.get(this.$config.serverHeaderTokenName)
      },
      prepareUrl: this.$config.fileApi.initUpload, // 文件预上传（初始化）地址
      fileAction: this.$config.fileApi.upload,
      initDownloadApi: this.$config.fileApi.initDownload,
      downloadApi: this.$config.fileApi.download,
      queryApi: this.$config.fileApi.query,
      fileList: []
    };
  },
  methods: {
    /**
     * @description: 处理上传文件前的操作处理。封装了hm文件系统的“文件上传初始化功能”
     * 1. 判断是否有自定义beforeUpload方法，如果没有直接返回“文件上传初始化功能”方法到下一步。
     * 2. 如果设置了beforeUpload方法，则判断自定义的beforeUpload的返回值分别处理
     *  a. 如果返回了promise则，添加“文件上传初始化功能”到下个then处理函数中。
     *  b. 如果返回了false，则代表自定义的beforeUpload执行不通过，把false往element中继续传递。
     *  c. 如果返回了true，则代表自定义的beforeUpload执行通过，则返回“文件上传初始化功能”方法到element中继续处理。
     * @param {File} file
     * @return: Promise
     */
    handleBeforeUpload (file) {
      if (this.format.length > 0) {
        const fileEnd = file.name.split('').reverse().join('').split('.')[0].split('').reverse().join('');
        if (!this.format.includes(fileEnd)) {
          if (!this.$attrs['format-error']) {
            this.$alert(`文件格式不正确，请上传后缀名为【${this.format.join('、')}】的文件！`, '提示', {
              confirmButtonText: '确定',
              type: 'warning',
              callback: () => {
              }
            });
            return false;
          } else {
            return this.$attrs['on-format-error'](file);
          }
        }
      }
      if (this.maxSize > 0 && file.size > this.maxSize * 1024) {
        if (!this.$attrs['format-error']) {
          this.$alert('文件大小不符合规范！', '提示', {
            confirmButtonText: '确定',
            type: 'warning',
            callback: () => {
            }
          });
          return false;
        } else {
          return this.$attrs['on-exceeded-size'](file);
        }
      }
      // 1.如果没有自定义beforeUpload事件，但定义文件预上传地址，则调用默认的“文件上传初始化功能”操作
      if (!this.$attrs['before-upload'] && this.prepareUrl) {
        return this.defaultBeforeUpload(file);
      }
      if (this.$attrs['before-upload']) {
      // 2.如果设置自定义beforeUpload事件，分三种返回值处理
        const before = this.$attrs['before-upload'](file);
        if (before && before.then && this.prepareUrl) { /* a.如果返回promise，则注册上传文件执行初始化步骤 */
          return before.then(() => {
            return this.defaultBeforeUpload(file);
          });
        } else if (before === false) { /* b.如果返回false，需要把false返回到最终处理的方法中  */
          return false;
        } else if (this.prepareUrl) { /* c.如果返回true，则需要把“文件上传初始化功能”方法返回到最终处理的方法中，到最终处理方法中先执行初始化上传操作  */
          return this.defaultBeforeUpload(file);
        }
      }
    },
    /**
     * @description: “文件上传初始化功能”
     * 按照文件系统设计，每次执行上传需要调用一次初始化操作。
     * @return: Promise
     */
    defaultBeforeUpload (file) {
      // 临时添加随机数用于避免重复
      this.$attrs.data.md5 = new Date().getTime();

      // 读取文件名设置到请求数据中
      this.$attrs.data.name = file.name;
      return this.initUpload(this.$attrs.data)
        .then((rsp) => {
          this.$attrs.data.fileId = rsp.data.fileId;
        })
        .catch((err) => {
          console.error('初始化上传失败！', err);
          return Promise.reject(new Error('初始化上传失败！'));
        });
    },
    /**
     * @description: 调用初始化接口
     * @param fd {object} 提交参数内容
     * @return:返回promise对象
     */
    initUpload (fd) {
      // 添加header用于传递权限信息
      return this.$axios.post(`${this.prepareUrl}`, fd, { headers: this.$attrs.headers });
    },
    /* 对element UI upload组件的methods进行封装 start */
    /**
     * @description: 清空已上传的文件列表（该方法不支持在 before-upload 中调用）
     */
    clearFiles () {
      this.$refs.fileServeUpload.clearFiles();
    },
    /**
     * @description: 取消上传请求
     * @param {File} file fileList 中的 file 对象
     */
    abort (file) {
      this.$refs.fileServeUpload.abort(file);
    },
    /**
     * @description:手动上传文件列表
     */
    submit () {
      this.$refs.fileServeUpload.submit();
    },
    /**
     * @description: 上传成功
     * @param {*} response
     * @param {*} file
     * @param {*} fileList
     * @return {*}
     */
    handleSuccess (response, file, fileList) {
      const _this = this;
      if (_this.successAlertMsg) {
        _this.$alert(_this.successAlertMsg, '提示', {
          confirmButtonText: '确定',
          type: 'success',
          callback: () => {
          }
        });
      }
      if (_this.multipleFile) {
        this.fileList.push({ name: response.name, id: response.fileId, path: '', error: false });
        this.$emit('input', this.value.concat(response.fileId));
      } else {
        this.fileList = [{ name: response.name, id: response.fileId, path: '', error: false }];
        this.$emit('input', response.fileId);
      }
      if (_this.$attrs['on-success']) {
        return _this.$attrs['on-success'](response, file, fileList);
      }
    },
    /**
     * @description: 上传失败
     * @param {*}
     * @return {*}
     */
    handleError (err, file, fileList) {
      const _this = this;
      _this.loading = _this.$loading({
        target: '.l-page'
      });
      if (_this.$attrs['on-error']) {
        return _this.$attrs['on-error'](err, file, fileList);
      } else {
        try {
          const { message } = err;
          const { errorMsg } = JSON.parse(message);
          _this.$alert(errorMsg, '提示', {
            confirmButtonText: '确定',
            type: 'error',
            callback: () => {
            }
          });
          _this.loading.close();
        } catch (error) {
          _this.loading.close();
        }
      }
    },
    /**
     * @description: 点击已上传的文件链接，下载文件
     * @param {*} file
     * @return {*}
     */
    handlePreview (file) {
      return this.initDownload(file.id)
        .then((rsp) => {
          const filePath = rsp.data.data;
          this.$axios.get(`${this.downloadApi}/${filePath}`, { responseType: 'blob' })
            .then((res) => {
              const fileStream = res.data;
              // 读取文件名
              const fileName = decodeURIComponent(escape(res.headers['content-disposition'].split('=')[1]));
              this.$HMUtil.downloadStreamFile(fileStream, fileName);
            })
            .catch((err) => {
              console.error('下载文件失败！', err);
            });
        })
        .catch((err) => {
          console.error('初始化下载文件失败！', err);
        });
    },
    /**
     * @description: 文件状态改变时的钩子，添加文件、上传成功和上传失败时都会被调用
     * @param {*} file
     * @param {*} fileList
     * @return {*}
     */
    handleChangeFile (file, fileList) {
      if (!this.multipleFile && file.status === 'ready' && fileList.length > 1) {
        fileList.splice(0, 1);
      };
      if (this.$attrs['on-change']) {
        return this.$attrs['on-change'](file, fileList);
      }
    },
    /**
     * @description: 文件列表移除文件时的钩子
     * @param {*} file
     * @param {*} fileList
     * @return {*}
     */
    handleRemove (file, fileList) {
      this.$emit('input', fileList);
      if (this.$attrs['on-remove']) {
        return this.$attrs['on-remove'](file, fileList);
      }
    },
    /* 对element UI upload组件的methods进行封装 end */
    /**
     * @description: 初始化下载
     * @param {*} fileId
     * @return {*}
     */
    initDownload (fileId) {
      return this.$axios.get(`${this.initDownloadApi}/${fileId}`);
    },
    /**
     * @description: 根据绑定的value，迭代初始化文件信息
     * @return {*}
     */
    handleIterationInitFile () {
      const _this = this;

      if (this.multipleFile) {
        _this.value.forEach(item => {
          _this.initFile(item);
        });
      } else if (_this.getType((_this.value)) === 'string') {
        _this.initFile(_this.value);
      }
    },
    getType (value) {
      return typeof value;
    },
    /**
     * @description: 初始化获取文件信息
     * @param {*} fileId
     * @return {*}
     */
    initFile (fileId) {
      if (!fileId) {
        return;
      }
      this.$axios.get(`${this.queryApi}/${fileId}`)
        .then(rsp => {
          this.fileList.push({ ...rsp.data, url: rsp.data.path });
        })
        .catch(err => {
          this.fileList.push({
            name: fileId,
            path: null,
            error: true
          });
          console.error('文件信息加载失败：' + fileId, err);
        });
    }
  },
  watch: {
    value (newValue) {
      if (newValue && this.fileList.length === 0) {
        this.handleIterationInitFile();
      }
    }
  },
  created () {
    if (this.value) {
      this.handleIterationInitFile();
    }
  }
};
</script>
